import { sourTwMerge } from '@plaidev/sour-react';
import {
  type FC,
  forwardRef,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type PropsWithChildren,
  type MouseEvent as ReactMouseEvent,
  type TouchEventHandler,
} from 'react';

/** v5 admin-ui 相当の網掛けストライプ（KARTE admin グローバル CSS に非依存） */
export const ADMIN_UI_STRIPE_STYLE: React.CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(-45deg, #ffffff40, #ffffff40 6px, #12a0a040 6px, #12a0a040 12px)',
};

export const PopoverMenuContent: FC<PropsWithChildren<{ className?: string }>> = ({ children, className, ...props }) => {
  return (
    <div
      {...props}
      className={sourTwMerge(
        'flex flex-col max-h-[424px] min-w-[120px] overflow-y-auto bg-default rounded-lg shadow-16 py-2 animate-fade-in-150 select-none',
        className,
      )}
    >
      {children}
    </div>
  );
};

export type PopoverMenuItemProps = PropsWithChildren<{
  onClick?: MouseEventHandler<HTMLDivElement>;
  /** onTouchEnd で移動量を判定する場合の起点記録用。 */
  onTouchStart?: TouchEventHandler<HTMLDivElement>;
  /**
   * Radix HoverCardTrigger の asChild アンカーにすると、合成された onTouchStart の
   * preventDefault によりタップ後の互換 click が発火しなくなる（"prevent focus event on
   * touch devices"）。タッチ経路のアクションが必要な場合はこれで拾う。
   */
  onTouchEnd?: TouchEventHandler<HTMLDivElement>;
  className?: string;
  disabled?: boolean;
}>;

// Radix の asChild（HoverCardTrigger 等）のアンカーに使えるよう ref を div へ転送する。
// 転送しないと Popper がアンカーを測れず、カードの位置が決まらない。
export const PopoverMenuItem = forwardRef<HTMLDivElement, PopoverMenuItemProps>(function PopoverMenuItem(
  { children, className, disabled, onClick, onTouchStart, onTouchEnd, ...props },
  forwardedRef,
) {
  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (!onClick || disabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onClick(event as unknown as ReactMouseEvent<HTMLDivElement>);
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: onClick と対で role="button" / tabIndex / onKeyDown を条件付き付与しており、静的解析では相関を証明できないため
    <div
      {...props}
      ref={forwardedRef}
      aria-disabled={disabled}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onClick={disabled ? undefined : onClick}
      onTouchStart={disabled ? undefined : onTouchStart}
      onTouchEnd={disabled ? undefined : onTouchEnd}
      onKeyDown={onClick ? handleKeyDown : undefined}
      className={sourTwMerge(
        'flex items-center gap-3 h-9 w-full font-medium px-4 outline-none transition-colors text-default text-sm truncate',
        disabled ? 'cursor-not-allowed opacity-40' : 'hover:cursor-pointer hover:bg-action-subtle-hover active:bg-action-subtle-active',
        className,
      )}
    >
      {children}
    </div>
  );
});

export const PopoverMenuSeparator: FC<PropsWithChildren<{ className?: string }>> = ({ className, ...props }) => {
  return (
    <div {...props} className={sourTwMerge('shrink-0 h-px bg-subtle my-2', className)}>
      <hr className="hidden" />
    </div>
  );
};

export const PopoverMenuIcon: FC<PropsWithChildren<{ className?: string }>> = ({ children, className, ...props }) => {
  return (
    <span {...props} className={sourTwMerge('shrink-0 inline-block w-[1em] text-center', className)}>
      {children}
    </span>
  );
};
